import { ORIGINAL_RENDERING_MANIFEST } from "../data/generated/original-rendering-manifest";
import {
  originalHpGaugeSegments,
  originalMapTileSourceRect,
  originalMpGaugeSegments,
  originalPlayerSpriteLayout,
  type GaugeSegment,
  type OriginalDirection,
  type PlayerSpriteLayout,
  type Rect
} from "./assetLayoutRuntime";

export type RasterOperation = "SRCCOPY" | "SRCAND" | "SRCINVERT";

export type RenderingBltOperation = {
  source: Rect;
  destination: Rect;
  rasterOp: RasterOperation;
  evidence: {
    callsiteId: string;
    statementId: string;
    procedure: string;
  };
};

export type PlayerRenderingComposition = {
  layout: PlayerSpriteLayout;
  mask: RenderingBltOperation;
  source: RenderingBltOperation;
};

export type GaugeRenderingSegment = GaugeSegment & {
  rasterOp: RasterOperation;
  evidenceCallsiteId: string;
  evidenceProcedure: string;
};

type RenderingCallsite = {
  id: string;
  procedure: string;
  statementId: string;
  evidenceKind: string;
  semanticStatus: string;
  rasterOp: RasterOperation;
  arguments: {
    destHdc: string;
    x: string;
    y: string;
    width: string;
    height: string;
    srcHdc: string;
    srcX: string;
    srcY: string;
    rop: string;
  };
};

const DECODED_BITBLT_CALLS = ORIGINAL_RENDERING_MANIFEST.decodedBitBltCallsites as unknown as readonly RenderingCallsite[];

function requireCallsite(
  label: string,
  predicate: (callsite: RenderingCallsite) => boolean
): RenderingCallsite {
  const callsite = DECODED_BITBLT_CALLS.find(
    (candidate) =>
      candidate.evidenceKind === "converted_vb20_text_source_callsite" &&
      candidate.semanticStatus === "decoded_source_api_callsite" &&
      predicate(candidate)
  );
  if (!callsite) {
    throw new Error(`Missing original rendering callsite: ${label}`);
  }
  return callsite;
}

const MAP_TILE_CALLSITE = requireCallsite(
  "MapDraw_First tile SRCCOPY",
  (callsite) =>
    callsite.procedure === "MapDraw_First" &&
    callsite.arguments.srcHdc === "Main!MapChar.hDC" &&
    callsite.rasterOp === "SRCCOPY"
);
const PLAYER_MASK_CALLSITE = requireCallsite(
  "ManDraw player SRCAND mask",
  (callsite) => callsite.procedure === "ManDraw" && callsite.rasterOp === "SRCAND"
);
const PLAYER_SOURCE_CALLSITE = requireCallsite(
  "ManDraw player SRCINVERT source",
  (callsite) => callsite.procedure === "ManDraw" && callsite.rasterOp === "SRCINVERT"
);
const HP_FILLED_CALLSITE = requireCallsite(
  "HPDraw filled SRCCOPY",
  (callsite) => callsite.procedure === "HPDraw" && callsite.arguments.srcY === "0"
);
const HP_EMPTY_CALLSITE = requireCallsite(
  "HPDraw empty SRCCOPY",
  (callsite) => callsite.procedure === "HPDraw" && callsite.arguments.srcY === "12"
);
const MP_FILLED_CALLSITE = requireCallsite(
  "MPDraw filled SRCCOPY",
  (callsite) => callsite.procedure === "MPDraw" && callsite.arguments.srcY === "24"
);
const MP_EMPTY_CALLSITE = requireCallsite(
  "MPDraw empty SRCCOPY",
  (callsite) => callsite.procedure === "MPDraw" && callsite.arguments.srcY === "36"
);

export const ORIGINAL_RENDERING_RUNTIME_CONSUMPTION = {
  manifest: "docs/original-rendering-manifest.json",
  generatedManifestModule: "web/src/data/generated/original-rendering-manifest.ts",
  consumesDecodedRendering: true,
  sourceBackedStatus: ORIGINAL_RENDERING_MANIFEST.status,
  sourceBackedCriteria: {
    renderingCallsiteManifestSourceBacked: ORIGINAL_RENDERING_MANIFEST.criteria.renderingCallsiteManifestSourceBacked,
    actualBitBltCallsitesDecoded: ORIGINAL_RENDERING_MANIFEST.criteria.actualBitBltCallsitesDecoded,
    bitBltArgumentsDecoded: ORIGINAL_RENDERING_MANIFEST.criteria.bitBltArgumentsDecoded,
    bitBltCallsitesCarryStatementIds: ORIGINAL_RENDERING_MANIFEST.criteria.bitBltCallsitesCarryStatementIds,
    assetLayoutManifestParitySatisfied: ORIGINAL_RENDERING_MANIFEST.criteria.assetLayoutManifestParitySatisfied
  },
  consumedCallsiteIds: {
    mapTile: MAP_TILE_CALLSITE.id,
    playerMask: PLAYER_MASK_CALLSITE.id,
    playerSource: PLAYER_SOURCE_CALLSITE.id,
    hpFilled: HP_FILLED_CALLSITE.id,
    hpEmpty: HP_EMPTY_CALLSITE.id,
    mpFilled: MP_FILLED_CALLSITE.id,
    mpEmpty: MP_EMPTY_CALLSITE.id
  }
} as const;

export function originalRenderingRuntimeConsumesDecodedRendering(): boolean {
  return ORIGINAL_RENDERING_RUNTIME_CONSUMPTION.consumesDecodedRendering;
}

export function originalMapTileRenderingOperation(
  tileIndex: number,
  destinationX: number,
  destinationY: number
): RenderingBltOperation {
  return {
    source: originalMapTileSourceRect(tileIndex),
    destination: { x: destinationX, y: destinationY, width: 32, height: 32 },
    rasterOp: "SRCCOPY",
    evidence: {
      callsiteId: MAP_TILE_CALLSITE.id,
      statementId: MAP_TILE_CALLSITE.statementId,
      procedure: MAP_TILE_CALLSITE.procedure
    }
  };
}

export function originalPlayerRenderingComposition(
  direction: OriginalDirection,
  frameIndex: number,
  charType: number,
  destinationX: number,
  destinationY: number
): PlayerRenderingComposition {
  const layout = originalPlayerSpriteLayout(direction, frameIndex, charType);
  const destination = { x: destinationX, y: destinationY, width: layout.destination.width, height: layout.destination.height };
  return {
    layout,
    mask: {
      source: layout.mask,
      destination,
      rasterOp: "SRCAND",
      evidence: {
        callsiteId: PLAYER_MASK_CALLSITE.id,
        statementId: PLAYER_MASK_CALLSITE.statementId,
        procedure: PLAYER_MASK_CALLSITE.procedure
      }
    },
    source: {
      source: layout.source,
      destination,
      rasterOp: "SRCINVERT",
      evidence: {
        callsiteId: PLAYER_SOURCE_CALLSITE.id,
        statementId: PLAYER_SOURCE_CALLSITE.statementId,
        procedure: PLAYER_SOURCE_CALLSITE.procedure
      }
    }
  };
}

export function originalHpRenderingSegments(actorIndex: number, hp: number, maxHp: number): GaugeRenderingSegment[] {
  return originalHpGaugeSegments(actorIndex, hp, maxHp).map((segment) => ({
    ...segment,
    rasterOp: "SRCCOPY" as const,
    evidenceCallsiteId: segment.source.y === 0 ? HP_FILLED_CALLSITE.id : HP_EMPTY_CALLSITE.id,
    evidenceProcedure: "HPDraw"
  }));
}

export function originalMpRenderingSegments(actorIndex: number, mp: number, maxMp: number): GaugeRenderingSegment[] {
  return originalMpGaugeSegments(actorIndex, mp, maxMp).map((segment) => ({
    ...segment,
    rasterOp: "SRCCOPY" as const,
    evidenceCallsiteId: segment.source.y === 24 ? MP_FILLED_CALLSITE.id : MP_EMPTY_CALLSITE.id,
    evidenceProcedure: "MPDraw"
  }));
}
